import { useEffect, useState } from 'react';
import { brandAPI } from '../lib/api';

// Brand identity (wordmark + logo) driven by the CMS `settings` doc, fetched
// once and shared across the app. Cached in localStorage for instant paint so
// the shell never flashes a placeholder on reload.
const FALLBACK = { acronym: '', name: '', wordmark: '', default_wordmark: 'LMS', logo: '', logo_light: '', palette: {} };

let cache = null;
let inflight = null;

function readLS() {
  try {
    return JSON.parse(localStorage.getItem('lms_brand')) || null;
  } catch {
    return null;
  }
}

// The whole LMS UI is built on var(--lms-*) (Tailwind maps lms-* → these vars),
// so writing the CMS-managed palette onto :root re-themes the entire app.
// Missing keys are left untouched → the index.css defaults stay in force.
const LMS_KEYS = [
  'bg', 'surface', 'raised', 'border', 'text', 'muted', 'faint',
  'accent', 'accent_ink', 'danger', 'success', 'warning', 'info',
  'sidebar', 'topbar', 'input',
];
function applyPalette(palette) {
  if (!palette) return;
  const root = document.documentElement;
  LMS_KEYS.forEach((k) => {
    const v = palette[k];
    if (v) root.style.setProperty(`--lms-${k.replace(/_/g, '-')}`, v);
  });
}

// Instant paint on reload from the last cached palette, before React mounts.
applyPalette((readLS() || {}).palette);

// Drop the cached brand and refetch; every mounted useBrand() repaints (used
// after Admin → Settings → Branding saves a new wordmark).
export function refreshBrand() {
  cache = null;
  inflight = null;
  try {
    localStorage.removeItem('lms_brand');
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event('lms-brand-changed'));
}

export function useBrand() {
  const [brand, setBrand] = useState(cache || readLS() || FALLBACK);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const onChange = () => setVersion((v) => v + 1);
    window.addEventListener('lms-brand-changed', onChange);
    return () => window.removeEventListener('lms-brand-changed', onChange);
  }, []);

  useEffect(() => {
    if (cache) {
      setBrand(cache);
      return undefined;
    }
    let alive = true;
    const p =
      inflight ||
      (inflight = brandAPI
        .get()
        .then(({ data }) => {
          cache = data;
          try {
            localStorage.setItem('lms_brand', JSON.stringify(data));
          } catch {
            /* ignore quota/privacy errors */
          }
          // The tab always needs a title; the on-page wordmark may be empty.
          document.title = data.wordmark || data.default_wordmark || data.name || 'LMS';
          applyPalette(data.palette);
          return data;
        })
        .catch(() => cache || readLS() || FALLBACK)
        .finally(() => {
          inflight = null;
        }));
    p.then((d) => {
      if (alive) setBrand(d);
    });
    return () => {
      alive = false;
    };
  }, [version]);

  return brand;
}
